import { fromCanonicalKg, toCanonicalKg, type UnitsPreference, parseLocaleNumber } from "@heraion/shared";

/**
 * Re-express a weight the athlete can see, from one display unit into the
 * other, going through canonical kg.
 *
 * ⛔ It exists because the alternative — flipping the unit label and leaving
 * the digits — silently multiplies the stored load by 2.2. The cell is very
 * often NOT something the athlete typed: hydrating a past session or copying
 * from history writes `String(fromCanonicalKg(storedKg, unit))` into it, so a
 * label swap turns 220.5 lb into 220.5 kg with nothing on screen to show for
 * it (`MX-6` on the MAX editor, `LG-48` in the logger).
 *
 * ⛔ It converts unconditionally, and that is the decision, not an oversight:
 * provenance ("did YOU type these digits?") is not reliably knowable at this
 * point — `weightSource` answers where the weight came from when it was first
 * logged, which can say "manual" about digits the system has since rewritten.
 * So the tie goes to the failure the athlete can SEE: converting something
 * they typed shows a wrong number they retype, while relabelling something the
 * system seeded looks right and saves wrong.
 *
 * Text that is empty or does not parse comes back untouched — a half-typed
 * cell is not an error to correct, and blanking it would lose keystrokes.
 * Parsing tolerates the comma decimal separator (it/fr/de keyboards); the way
 * back is a bare `String(...)`, the convention every sibling write into a
 * draft cell already follows.
 *
 * ⭐ `seedKg` is the stored weight this cell was FILLED FROM, when the caller
 * knows it. While the text is still that seed's display, the conversion
 * restarts from the exact kilos instead of from the rounded pounds on screen
 * — because a display is a view of a value, not the value: 100 kg shown as
 * 220.5 lb reads back as 100.017, and 220.5 lb genuinely IS 100.017 kg, so no
 * rounding rule recovers the 100 (`LG-85`). Omit it and behaviour is as before.
 *
 * ⛔ This detector lives HERE and not in the callers, and that is the point of
 * CN-26: `useMaxEditor` carried its own copy from 26/08, under a comment that
 * called itself «scorciatoia PRIMA del primitivo condiviso» — this is that
 * primitive, and the shortcut is gone.
 * ⚠️ The plate calculator does NOT come through here: it works on numbers, not
 * on cell text, and it knows it was seeded explicitly (`CalcState.placedKg`)
 * instead of having to infer it from a string compare. Same rule, a better
 * detector — a text compare is the weakest way to ask «did you touch this?»,
 * and it is used only where nothing stronger is available.
 */
export function convertWeightText(
  text: string,
  from: UnitsPreference,
  to: UnitsPreference,
  seedKg: number | null = null,
): string {
  if (from === to) return text;
  if (seedKg !== null && text === String(fromCanonicalKg(seedKg, from)))
    return String(fromCanonicalKg(seedKg, to));
  const parsed = parseLocaleNumber(text);
  if (!Number.isFinite(parsed)) return text;
  return String(fromCanonicalKg(toCanonicalKg(parsed, from), to));
}
